import { AppBar, Toolbar, useMediaQuery, useTheme } from '@mui/material';
import { FC, memo, useContext, useEffect, useState } from 'react';
import ReactGA from 'react-ga4';
import { useNavigate, useParams } from 'react-router';
import { SCREEN_NAMES } from 'src/constants/screenNames';
import LicenseContext from 'src/contexts/LicenseContext';
import datetimeDecorator from 'src/decorators/datetime.decorator';
import { CompanyEntity } from 'src/entities/admin/companyEntity';

import AppBarLgMenuComponent from './AppBarLgMenu.component';
import AppBarLogoComponent from './AppBarLogo.component';
import AppBarMdMenuComponent from './AppBarMdMenu.component';
import AppBarSmMenuComponent from './AppBarSmMenu.component';

const AppBarContainerComponent: FC = memo(() => {
  const licenseContext = useContext(LicenseContext);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isMediumScreen = useMediaQuery(theme.breakpoints.between('sm', 'lg'));
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));

  const navigate = useNavigate();
  const { startOn, endOn } = useParams();

  const [hideThisComponent, setHideThisComponent] = useState<boolean>(false);
  const [hideShowAllSelectMenuItem, setHideShowAllSelectMenuItem] = useState<boolean>(false);
  const [companies, setCompanies] = useState<CompanyEntity[]>([]);

  const [planningMenuAnchorEl, setPlanningMenuAnchorEl] = useState<null | HTMLElement>(null);
  const handlePlanningMenuOnClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setPlanningMenuAnchorEl(event.currentTarget);
  };
  const handlePlanningMenuOnClose = () => {
    setPlanningMenuAnchorEl(null);
  };

  const [settingMenuAnchorEl, setSettingMenuAnchorEl] = useState<null | HTMLElement>(null);
  const handleSettingMenuOnClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setSettingMenuAnchorEl(event.currentTarget);
  };
  const handleSettingMenuOnClose = () => {
    setSettingMenuAnchorEl(null);
  };

  const [accountMenuAnchorEl, setAccountMenuAnchorEl] = useState<null | HTMLElement>(null);
  const handleAccountMenuOnClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAccountMenuAnchorEl(event.currentTarget);
  };
  const handleAccountMenuOnClose = () => {
    setAccountMenuAnchorEl(null);
  };

  const buildPathWithStartOnAndEndOn = (plansOrTimelineOrOrders: string) => {
    if (startOn && endOn) {
      return `/${plansOrTimelineOrOrders}/${startOn}/${endOn}`;
    }

    if (licenseContext.startOn && licenseContext.endOn) {
      return `/${plansOrTimelineOrOrders}/${licenseContext.startOn}/${licenseContext.endOn}`;
    }

    const on = localStorage.getItem('planningOn') || datetimeDecorator.toYyyyMmDd(new Date());
    return `/${plansOrTimelineOrOrders}/${on}/${on}`;
  };

  const buildPathWithStartOn = (plansOrTimelineOrOrders: string) => {
    if (startOn) {
      return `/${plansOrTimelineOrOrders}/${startOn}`;
    }

    if (licenseContext.startOn) {
      return `/${plansOrTimelineOrOrders}/${licenseContext.startOn}`;
    }

    const on = localStorage.getItem('planningOn') || datetimeDecorator.toYyyyMmDd(new Date());
    return `/${plansOrTimelineOrOrders}/${on}`;
  };

  const navigateToDashboard = (isLogo: boolean) => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.HEADER, button_name: isLogo ? 'ロゴ' : SCREEN_NAMES.DASHBOARD });
    void navigate('/dashboard');
  };

  const navigateToKpis = () => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.HEADER, button_name: '実績参照' });
    void navigate('/kpis');
  };

  const navigateToPlanningMap = () => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.HEADER, button_name: '配車計画 - マップ' });
    void navigate(buildPathWithStartOnAndEndOn('plans'));
    handlePlanningMenuOnClose();
  };

  const navigateToPlanningTimeline = () => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.HEADER, button_name: '配車計画 - タイムライン' });
    void navigate(buildPathWithStartOnAndEndOn('timeline'));
    handlePlanningMenuOnClose();
  };

  const navigateToPlanningLane = () => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.HEADER, button_name: '配車計画 - レーン管理' });
    void navigate(buildPathWithStartOnAndEndOn('lanes'));
    handlePlanningMenuOnClose();
  };

  const navigateToConvenienceDispatches = () => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.HEADER, button_name: '配車計画 - 固定コース' });
    void navigate(buildPathWithStartOnAndEndOn('convenience-dispatches'));
    handlePlanningMenuOnClose();
  };

  const navigateToPlanningTrunkTransportation = () => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.HEADER, button_name: '配車計画 - 幹線輸送' });
    void navigate(buildPathWithStartOn('trunk-transportation'));
    handlePlanningMenuOnClose();
  };

  const navigateToOrders = () => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.HEADER, button_name: '案件一覧' });
    void navigate(buildPathWithStartOnAndEndOn('orders'));
  };

  const navigateToSalesOrders = () => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.HEADER, button_name: '受注一覧' });
    void navigate('/sales-orders');
  };

  const navigateToShipmentOrders = () => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.HEADER, button_name: '案件管理' });
    void navigate(buildPathWithStartOnAndEndOn('shipment-orders'));
  };

  const navigateToShipmentOrderLegs = () => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.HEADER, button_name: '配車一覧' });
    void navigate(buildPathWithStartOnAndEndOn('shipment-order-legs'));
  };

  const navigateToContracts = () => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.HEADER, button_name: '設定 - 契約管理' });
    void navigate('/contracts');
    handleSettingMenuOnClose();
  };

  const navigateToGarages = () => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.HEADER, button_name: '設定 - 車庫管理' });
    void navigate('/garages');
    handleSettingMenuOnClose();
  };

  const navigateToPartnerCompanies = () => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.HEADER, button_name: '設定 - 運送会社管理' });
    void navigate('/partner-companies');
    handleSettingMenuOnClose();
  };

  const navigateToTrucks = () => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.HEADER, button_name: '設定 - 車両管理' });
    void navigate('/trucks');
    handleSettingMenuOnClose();
  };

  const navigateToGroups = () => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.HEADER, button_name: '設定 - 車両グループ管理' });
    void navigate('/groups');
    handleSettingMenuOnClose();
  };

  const navigateToDrivers = () => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.HEADER, button_name: '設定 - ドライバー管理' });
    void navigate('/drivers');
    handleSettingMenuOnClose();
  };

  const navigateToPlaces = () => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.HEADER, button_name: '設定 - 地点管理' });
    void navigate('/places');
    handleSettingMenuOnClose();
  };

  const navigateToProducts = () => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.HEADER, button_name: '設定 - 商品管理' });
    void navigate('/products');
    handleSettingMenuOnClose();
  };

  const navigateToAreas = () => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.HEADER, button_name: '設定 - エリア管理' });
    void navigate('/areas');
    handleSettingMenuOnClose();
  };

  const navigateToOutsourcingContractSettings = () => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.HEADER, button_name: '設定 - 委託表設定' });
    void navigate('/outsourcing-contract-settings');
    handleSettingMenuOnClose();
  };

  const navigateToConfig = () => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.HEADER, button_name: '設定 - 計算エンジン設定' });
    void navigate('/config');
    handleSettingMenuOnClose();
  };

  const navigateToPerformanceManagement = () => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.HEADER, button_name: SCREEN_NAMES.PERFORMANCE_MANAGEMENT });
    void navigate('/performance-management');
  };

  const companyName = () => licenseContext?.config?.company_name;

  const userName = () => licenseContext?.config?.person_name || licenseContext?.config?.email;

  const useTrunkTransportation = licenseContext?.config?.use_trunk_transportation;
  const useLogForSales = licenseContext?.config?.use_log_for_sales;
  const useOutsourcingContractPdf = licenseContext.config?.use_outsourcing_contract_pdf;

  const navigateToAccounts = () => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.HEADER, button_name: 'アカウント - アカウント情報' });
    void navigate('/accounts');
    handleAccountMenuOnClose();
  };

  const isAdmin = () => licenseContext?.config?.role && licenseContext?.config?.role?.name === 'admin';

  const navigateToCompany = () => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.HEADER, button_name: 'アカウント - 会社情報' });
    void navigate('/company');
    handleAccountMenuOnClose();
  };

  const logout = () => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.HEADER, button_name: 'アカウント - ログアウト' });
    licenseContext.logout();
    handleAccountMenuOnClose();
  };

  useEffect(() => {
    const hideShowAllSelectMenuItemPaths = [
      '/config',
      '/import'
    ];

    setHideShowAllSelectMenuItem(
      hideShowAllSelectMenuItemPaths.includes(window.location.pathname)
    );
  }, [window.location.pathname]);

  useEffect(() => {
    const hideThisComponentPaths = [
      '/accounts',
      '/company',
    ];

    const isHideThisComponentPath = hideThisComponentPaths.includes(window.location.pathname);
    const notHaveSelectableCompanies = companies.length < 2;

    setHideThisComponent(isHideThisComponentPath || notHaveSelectableCompanies);
  }, [companies, window.location.pathname]);

  useEffect(() => {
    setCompanies(
      licenseContext?.config?.selectable_companies ?? []
    );
  }, [licenseContext]);
  return (
    <AppBar
      position="fixed"
      sx={{
        backgroundColor: theme.colors.alpha.white[100],
      }}
    >
      {(isLargeScreen) && (
        <Toolbar
          sx={{
            display: {
              xs: 'none',
              sm: 'none',
              md: 'none',
              lg: 'flex',
              xl: 'flex',
            },
          }}
        >
          <AppBarLogoComponent
            companies={companies}
            navigateToDashboard={navigateToDashboard}
            hideShowAllSelectMenuItem={hideShowAllSelectMenuItem}
            hideThisComponent={hideThisComponent}
          />
          <AppBarLgMenuComponent
            navigateToDashboard={navigateToDashboard}
            navigateToKpis={navigateToKpis}
            handlePlanningMenuOnClick={handlePlanningMenuOnClick}
            planningMenuAnchorEl={planningMenuAnchorEl}
            handlePlanningMenuOnClose={handlePlanningMenuOnClose}
            navigateToPlanningMap={navigateToPlanningMap}
            navigateToPlanningTimeline={navigateToPlanningTimeline}
            navigateToConvenienceDispatches={navigateToConvenienceDispatches}
            navigateToPlanningLane={navigateToPlanningLane}
            navigateToOrders={navigateToOrders}
            handleSettingMenuOnClick={handleSettingMenuOnClick}
            settingMenuAnchorEl={settingMenuAnchorEl}
            handleSettingMenuOnClose={handleSettingMenuOnClose}
            navigateToContracts={navigateToContracts}
            navigateToGarages={navigateToGarages}
            navigateToPartnerCompanies={navigateToPartnerCompanies}
            navigateToTrucks={navigateToTrucks}
            navigateToGroups={navigateToGroups}
            navigateToDrivers={navigateToDrivers}
            navigateToPlaces={navigateToPlaces}
            navigateToProducts={navigateToProducts}
            navigateToAreas={navigateToAreas}
            useOutsourcingContractPdf={useOutsourcingContractPdf}
            navigateToOutsourcingContractSettings={navigateToOutsourcingContractSettings}
            navigateToConfig={navigateToConfig}
            handleAccountMenuOnClick={handleAccountMenuOnClick}
            companyName={companyName}
            userName={userName}
            accountMenuAnchorEl={accountMenuAnchorEl}
            handleAccountMenuOnClose={handleAccountMenuOnClose}
            navigateToAccounts={navigateToAccounts}
            isAdmin={isAdmin}
            navigateToCompany={navigateToCompany}
            logout={logout}
            useTrunkTransportation={useTrunkTransportation}
            navigateToPlanningTrunkTransportation={navigateToPlanningTrunkTransportation}
            useLogForSales={useLogForSales}
            navigateToSalesOrders={navigateToSalesOrders}
            navigateToShipmentOrders={navigateToShipmentOrders}
            navigateToShipmentOrderLegs={navigateToShipmentOrderLegs}
            appEnv={licenseContext.config?.app_env}
            navigateToPerformanceManagement={navigateToPerformanceManagement}
          />
        </Toolbar>
      )}
      {(isMediumScreen) && (
        <Toolbar
          sx={{
            display: {
              xs: 'none',
              sm: 'flex',
              md: 'flex',
              lg: 'none',
              xl: 'none',
            }
          }}
        >
          <AppBarLogoComponent
            companies={companies}
            navigateToDashboard={navigateToDashboard}
            hideShowAllSelectMenuItem={hideShowAllSelectMenuItem}
            hideThisComponent={hideThisComponent}
          />
          <AppBarMdMenuComponent
            navigateToKpis={navigateToKpis}
            handlePlanningMenuOnClick={handlePlanningMenuOnClick}
            planningMenuAnchorEl={planningMenuAnchorEl}
            handlePlanningMenuOnClose={handlePlanningMenuOnClose}
            navigateToPlanningMap={navigateToPlanningMap}
            navigateToPlanningTimeline={navigateToPlanningTimeline}
            navigateToConvenienceDispatches={navigateToConvenienceDispatches}
            navigateToPlanningLane={navigateToPlanningLane}
            navigateToOrders={navigateToOrders}
            handleSettingMenuOnClick={handleSettingMenuOnClick}
            settingMenuAnchorEl={settingMenuAnchorEl}
            handleSettingMenuOnClose={handleSettingMenuOnClose}
            navigateToContracts={navigateToContracts}
            navigateToGarages={navigateToGarages}
            navigateToPartnerCompanies={navigateToPartnerCompanies}
            navigateToTrucks={navigateToTrucks}
            navigateToGroups={navigateToGroups}
            navigateToDrivers={navigateToDrivers}
            navigateToPlaces={navigateToPlaces}
            navigateToProducts={navigateToProducts}
            navigateToAreas={navigateToAreas}
            useOutsourcingContractPdf={useOutsourcingContractPdf}
            navigateToOutsourcingContractSettings={navigateToOutsourcingContractSettings}
            navigateToConfig={navigateToConfig}
            handleAccountMenuOnClick={handleAccountMenuOnClick}
            accountMenuAnchorEl={accountMenuAnchorEl}
            handleAccountMenuOnClose={handleAccountMenuOnClose}
            navigateToAccounts={navigateToAccounts}
            isAdmin={isAdmin}
            navigateToCompany={navigateToCompany}
            logout={logout}
            useTrunkTransportation={useTrunkTransportation}
            navigateToPlanningTrunkTransportation={navigateToPlanningTrunkTransportation}
            useLogForSales={useLogForSales}
            navigateToSalesOrders={navigateToSalesOrders}
            navigateToShipmentOrders={navigateToShipmentOrders}
            navigateToShipmentOrderLegs={navigateToShipmentOrderLegs}
            appEnv={licenseContext.config?.app_env}
            navigateToPerformanceManagement={navigateToPerformanceManagement}
          />
        </Toolbar>
      )}
      {isSmallScreen && (
        <Toolbar
          sx={{
            display: {
              xs: 'flex',
              sm: 'none',
              md: 'none',
              lg: 'none',
              xl: 'none',
            }
          }}
        >
          <AppBarLogoComponent
            companies={companies}
            navigateToDashboard={navigateToDashboard}
            hideShowAllSelectMenuItem={hideShowAllSelectMenuItem}
            hideThisComponent={hideThisComponent}
          />
          <AppBarSmMenuComponent
            navigateToKpis={navigateToKpis}
            handlePlanningMenuOnClick={handlePlanningMenuOnClick}
            planningMenuAnchorEl={planningMenuAnchorEl}
            handlePlanningMenuOnClose={handlePlanningMenuOnClose}
            navigateToPlanningMap={navigateToPlanningMap}
            navigateToPlanningTimeline={navigateToPlanningTimeline}
            navigateToConvenienceDispatches={navigateToConvenienceDispatches}
            navigateToPlanningLane={navigateToPlanningLane}
            navigateToOrders={navigateToOrders}
            handleSettingMenuOnClick={handleSettingMenuOnClick}
            settingMenuAnchorEl={settingMenuAnchorEl}
            handleSettingMenuOnClose={handleSettingMenuOnClose}
            navigateToContracts={navigateToContracts}
            navigateToGarages={navigateToGarages}
            navigateToPartnerCompanies={navigateToPartnerCompanies}
            navigateToTrucks={navigateToTrucks}
            navigateToGroups={navigateToGroups}
            navigateToDrivers={navigateToDrivers}
            navigateToPlaces={navigateToPlaces}
            navigateToProducts={navigateToProducts}
            navigateToAreas={navigateToAreas}
            useOutsourcingContractPdf={useOutsourcingContractPdf}
            navigateToOutsourcingContractSettings={navigateToOutsourcingContractSettings}
            navigateToConfig={navigateToConfig}
            handleAccountMenuOnClick={handleAccountMenuOnClick}
            accountMenuAnchorEl={accountMenuAnchorEl}
            handleAccountMenuOnClose={handleAccountMenuOnClose}
            navigateToAccounts={navigateToAccounts}
            isAdmin={isAdmin}
            navigateToCompany={navigateToCompany}
            logout={logout}
            useTrunkTransportation={useTrunkTransportation}
            navigateToPlanningTrunkTransportation={navigateToPlanningTrunkTransportation}
            useLogForSales={useLogForSales}
            navigateToSalesOrders={navigateToSalesOrders}
            navigateToShipmentOrders={navigateToShipmentOrders}
            navigateToShipmentOrderLegs={navigateToShipmentOrderLegs}
            appEnv={licenseContext.config?.app_env}
            navigateToPerformanceManagement={navigateToPerformanceManagement}
          />
        </Toolbar>
      )}
    </AppBar>
  );
});

export default AppBarContainerComponent;
